/**
 * Context-sensitive mouse cursors. Each is a small inline SVG (data-URI) with a
 * white halo so the glyph reads on any terrain, applied to the WebGL canvas as
 * `cursor: url(...) hotX hotY, auto`. The action a right-click would perform
 * (gather / build / attack / embark / unload / move) drives which one shows.
 */
export type CursorAction = "default" | "move" | "attack" | "gather" | "build" | "embark" | "unload";

/** Wrap an inner SVG body (28×28 viewBox) into a data-URI cursor with a hotspot. */
function cur(body: string, hotX = 4, hotY = 4): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>${body}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${hotX} ${hotY}, auto`;
}

// shared white-halo arrow so every action still reads as a pointer
const ARROW = "<path d='M3 2 L3 19 L8 14 L11 21 L14 20 L11 13 L18 13 Z' fill='%23fff' stroke='%23111' stroke-width='1.2' stroke-linejoin='round'/>";
// a small badge in the lower-right corner carries the action glyph
const badge = (inner: string): string =>
  `<g transform='translate(13,13)'><circle cx='7' cy='7' r='7.5' fill='%23161616' stroke='%23fff' stroke-width='1.2'/>${inner}</g>`;

export const CURSORS: Record<CursorAction, string> = {
  default: "default",
  move: cur(ARROW),
  // crossed swords (red)
  attack: cur(ARROW + badge("<path d='M3 11 L11 3 M9 3 L11 3 L11 5 M3 4 L11 12 M3 6 L3 4 L5 4' stroke='%23ff5a5a' stroke-width='1.6' fill='none' stroke-linecap='round'/>")),
  // pickaxe / tool (green)
  gather: cur(ARROW + badge("<path d='M2 12 L12 2 M3 4 Q7 1 11 5' stroke='%236fdc5a' stroke-width='1.6' fill='none' stroke-linecap='round'/>")),
  // hammer (amber)
  build: cur(ARROW + badge("<path d='M7 13 L7 7 M4 5 L11 5 L11 8 L4 8 Z' stroke='%23ffc24a' stroke-width='1.5' fill='none' stroke-linejoin='round'/>")),
  // down chevron into a hull (cyan) — board the ship
  embark: cur(ARROW + badge("<path d='M7 2 L7 8 M4 5 L7 8 L10 5 M2 9 Q7 14 12 9' stroke='%234ec0e0' stroke-width='1.6' fill='none' stroke-linecap='round' stroke-linejoin='round'/>")),
  // up chevron out of a hull (cyan) — leave the ship
  unload: cur(ARROW + badge("<path d='M7 9 L7 3 M4 6 L7 3 L10 6 M2 10 Q7 14 12 10' stroke='%234ec0e0' stroke-width='1.6' fill='none' stroke-linecap='round' stroke-linejoin='round'/>")),
};
